import { useAuth0 } from "@auth0/auth0-react"
import {
    Configuration,
    BaseAPI,
    BASE_PATH,
    ManagementApi,
    ConsumerApi,
    TechnicianApi,
} from "../api"
import React from "react"

const BASE_URL = process.env.REACT_APP_API_URL || BASE_PATH

type ApiContextValue = {
    createApiClient: <T extends BaseAPI>(ApiClass: new (config: Configuration) => T) => T
    managementApi: ManagementApi
    consumerApi: ConsumerApi
    technicianApi: TechnicianApi
}

const ApiContext = React.createContext<ApiContextValue>({
    createApiClient: (() => {
        throw new Error("ApiProvider is missing in component tree")
    }) as ApiContextValue["createApiClient"],
    managementApi: {} as ManagementApi,
    consumerApi: {} as ConsumerApi,
    technicianApi: {} as TechnicianApi,
})

export const useApi = () => React.useContext(ApiContext)
export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0()

    const createConfiguration = React.useCallback(
        () =>
            new Configuration({
                basePath: BASE_URL,
                middleware: [
                    {
                        pre: async (context) => {
                            const token = await getAccessTokenSilently()
                            return {
                                ...context,
                                init: {
                                    ...context.init,
                                    headers: {
                                        ...(context.init?.headers || {}),
                                        Authorization: `Bearer ${token}`,
                                    },
                                },
                            }
                        },
                    },
                ],
            }),
        [getAccessTokenSilently]
    )

    const apiClientCache = React.useRef(new Map<unknown, BaseAPI>())

    const createApiClient = React.useCallback(
        <T extends BaseAPI>(ApiClass: new (config: Configuration) => T): T => {
            const cached = apiClientCache.current.get(ApiClass)
            if (cached) {
                return cached as T
            }
            const client = new ApiClass(createConfiguration())
            apiClientCache.current.set(ApiClass, client)
            return client
        },
        [createConfiguration]
    )

    const managementApi = createApiClient(ManagementApi)
    const consumerApi = createApiClient(ConsumerApi)
    const technicianApi = createApiClient(TechnicianApi)

    return (
        <ApiContext.Provider value={{ createApiClient, managementApi, consumerApi, technicianApi }}>
            {children}
        </ApiContext.Provider>
    )
}
